<script setup lang="ts">
  import { ref, computed, onMounted, watch } from "vue";
  import { useRouter } from "vue-router";
  import { useRapportenStore } from "../stores/rapporten";
  import { useRapportPaymentHolds } from "../composables/useRapportPaymentHolds";
  import { getRapportDetailsPath, isCertificaatBeschikbaar } from "../utils/rapportScope";
  import {
    fetchRapportPdfDocuments,
    getRapportPdfCompletedAt,
    getRapportPdfKey,
    resolveRapportPdfState,
    useRapportPdfDownloads,
    type RapportPdfDocument,
  } from "../composables/useRapportPdf";

  // Components
  import AppHeader from "../components/AppHeader.vue";
  import CustomTag from "../components/CustomTag.vue";

  // PrimeVue Components
  import Panel from "primevue/panel";
  import Select from "primevue/select";
  import ToggleButton from "primevue/togglebutton";
  import IconField from "primevue/iconfield";
  import InputIcon from "primevue/inputicon";
  import InputText from "primevue/inputtext";
  import Button from "primevue/button";

  // Types
  import {
    inspectieTypeLabels,
    rapportStatusLabels,
    type InspectieType,
    type RapportPaymentHold,
    type RapportStatus,
  } from "../../../../types";

  const router = useRouter();
  const rapportenStore = useRapportenStore();
  const { fetchRapportPaymentHolds } = useRapportPaymentHolds();

  // "Rapport na betaling": rapporten held back by an unpaid factuur get a badge.
  const holdsByRapportId = ref<Map<string, RapportPaymentHold>>(new Map());

  // Rapport PDF per scope (documents rows keyed by getRapportPdfKey), for the download row on each card.
  const pdfDocuments = ref<Map<string, RapportPdfDocument>>(new Map());
  const { busyKeys: pdfBusyKeys, downloadRapportPdf } = useRapportPdfDownloads();

  // Helper functions

  const statusLabels = rapportStatusLabels;

  // Filter state
  const searchQuery = ref("");
  const statusFilter = ref("all");
  const scopeFilters = ref({
    scope8: false,
    scope10: false,
    scope12: false,
  });

  // Pagination state
  const rows = ref(12);
  const displayCount = ref(rows.value);

  interface RapportCardRapport {
    id: string;
    inspectieRapportNr: string | null;
    created_at: string | null;
    locatie_snapshot?: {
      naam?: string | null;
      straat?: string | null;
      huisnr?: string | null;
      plaats?: string | null;
    } | null;
    inspectieStatus?: Array<{
      type: InspectieType;
      status: RapportStatus;
      afgemeld?: boolean;
      afgerond?: boolean;
      certificaat?: boolean;
      certificaat_pad?: string | null;
    }> | null;
  }

  interface RapportListEntry {
    id: string;
    rapport: RapportCardRapport;
    inspectieEntry: {
      type: InspectieType;
      status: RapportStatus;
      certificaatBeschikbaar: boolean;
      afgerond: boolean;
    };
    /** rapporten.zichtbaar = false: content not (yet) released. */
    vergrendeld: boolean;
    /** An unpaid rapport_na_betaling factuur is the reason for the lock. */
    paymentHold: boolean;
  }

  // Status filter options
  const statusOptions = [
    { label: "Alle", value: "all" },
    { label: "Nieuw", value: "nieuw" },
    { label: "Bezig", value: "bezig" },
    { label: "Voldoet", value: "voldoet" },
    { label: "Voldoet niet", value: "voldoet_niet" },
    { label: "Hersteld", value: "hersteld" },
    { label: "Verlopen", value: "verlopen" },
    { label: "Ingetrokken", value: "ingetrokken" },
  ];

  const buildRapportEntries = (): RapportListEntry[] => {
    const entries: RapportListEntry[] = [];
    const rapporten = rapportenStore.rapporten as unknown as RapportCardRapport[];

    for (const rapport of rapporten) {
      if (!Array.isArray(rapport.inspectieStatus)) continue;

      for (const inspectieEntry of rapport.inspectieStatus) {
        entries.push({
          id: `${rapport.id}-${inspectieEntry.type}`,
          rapport,
          inspectieEntry: {
            type: inspectieEntry.type,
            status: inspectieEntry.status,
            certificaatBeschikbaar: isCertificaatBeschikbaar(inspectieEntry),
            afgerond: Boolean(inspectieEntry.afgerond),
          },
          vergrendeld: holdsByRapportId.value.get(rapport.id)?.zichtbaar === false,
          paymentHold: holdsByRapportId.value.get(rapport.id)?.held ?? false,
        });
      }
    }

    return entries;
  };

  // Computed filtering
  const filteredRapporten = computed<RapportListEntry[]>(() => {
    const source = buildRapportEntries();
    if (source.length === 0) {
      return [];
    }

    // Status filter
    const statusFiltered: RapportListEntry[] = [];
    for (const entry of source) {
      if (statusFilter.value === "all") {
        statusFiltered.push(entry);
        continue;
      }

      if (entry.inspectieEntry.status === statusFilter.value) {
        statusFiltered.push(entry);
      }
    }

    // Scope filter
    const anyScope = scopeFilters.value.scope8 || scopeFilters.value.scope10 || scopeFilters.value.scope12;
    const scoped: RapportListEntry[] = [];
    for (const entry of statusFiltered) {
      if (!anyScope) {
        scoped.push(entry);
        continue;
      }

      const inspectieType = entry.inspectieEntry.type;
      const hasScope8 = scopeFilters.value.scope8 && inspectieType.startsWith("scope8");
      const hasScope10 = scopeFilters.value.scope10 && inspectieType.startsWith("scope10");
      const hasScope12 = scopeFilters.value.scope12 && inspectieType.startsWith("scope12");

      if (hasScope8 || hasScope10 || hasScope12) {
        scoped.push(entry);
      }
    }

    // Search filter
    if (!searchQuery.value.trim()) {
      return scoped;
    }

    const query = searchQuery.value.toLowerCase().trim();
    const searched: RapportListEntry[] = [];
    for (const entry of scoped) {
      const rapport = entry.rapport;
      const locatie = (rapport as any).locatie_snapshot;
      const inspectieLabel = String(inspectieTypeLabels[entry.inspectieEntry.type] || "").toLowerCase();

      if (
        (rapport.inspectieRapportNr && rapport.inspectieRapportNr.toLowerCase().includes(query)) ||
        (locatie?.naam && locatie.naam.toLowerCase().includes(query)) ||
        (locatie?.straat && locatie.straat.toLowerCase().includes(query)) ||
        (locatie?.plaats && locatie.plaats.toLowerCase().includes(query)) ||
        inspectieLabel?.includes(query)
      ) {
        searched.push(entry);
      }
    }

    return searched;
  });

  const displayedRapporten = computed(() => {
    return filteredRapporten.value.slice(0, displayCount.value);
  });

  const hasMore = computed(() => {
    return displayCount.value < filteredRapporten.value.length;
  });

  const loadMore = () => {
    displayCount.value = Math.min(displayCount.value + rows.value, filteredRapporten.value.length);
  };

  // Watch for filter changes and reset pagination
  watch(filteredRapporten, (value) => {
    displayCount.value = Math.min(rows.value, value.length);
  });

  // Helper functions
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "";
    try {
      return new Intl.DateTimeFormat("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(dateString));
    } catch {
      return "";
    }
  };

  interface ListPdfState {
    text: string;
    icon: string;
    tone: "default" | "success" | "warning" | "info" | "danger" | "muted";
    /** Show the download button (it makes the PDF first when needed). */
    downloadable: boolean;
    /** Show the "Betalen" link to the payment page. */
    payable: boolean;
  }

  const formatPdfDate = (date: Date | null) =>
    date ? new Intl.DateTimeFormat("nl-NL", { day: "numeric", month: "short", year: "numeric" }).format(date) : "";

  const getPdfKey = (entry: RapportListEntry) => getRapportPdfKey(entry.rapport.id, entry.inspectieEntry.type);

  /**
   * What the card says about the rapport PDF. Freshness is only known on the
   * detail page; here a completed PDF shows its date and the click checks
   * whether a newer one has to be made first.
   */
  const getListPdfState = (entry: RapportListEntry): ListPdfState => {
    const none = { downloadable: false, payable: false };
    if (entry.vergrendeld) {
      return entry.paymentHold
        ? { ...none, text: "PDF vrij na betaling", icon: "pi pi-lock", tone: "warning", payable: true }
        : { ...none, text: "PDF nog niet vrijgegeven", icon: "pi pi-lock", tone: "muted" };
    }
    if (!entry.inspectieEntry.afgerond) {
      return { ...none, text: "PDF volgt na afronding", icon: "pi pi-clock", tone: "muted" };
    }
    if (pdfBusyKeys.value.has(getPdfKey(entry))) {
      return { ...none, text: "Nieuwste PDF ophalen…", icon: "pi pi-spin pi-spinner", tone: "info", downloadable: true };
    }

    const document = pdfDocuments.value.get(getPdfKey(entry));
    switch (resolveRapportPdfState(document, null)) {
      case "ready":
        return {
          ...none,
          text: `PDF van ${formatPdfDate(getRapportPdfCompletedAt(document))}`,
          icon: "pi pi-file-pdf",
          tone: "default",
          downloadable: true,
        };
      case "generating":
        return { ...none, text: "PDF wordt gemaakt…", icon: "pi pi-spin pi-spinner", tone: "info", downloadable: true };
      case "queued":
        return { ...none, text: "PDF staat in de wachtrij", icon: "pi pi-clock", tone: "info", downloadable: true };
      case "error":
        return { ...none, text: "PDF maken mislukt, probeer opnieuw", icon: "pi pi-exclamation-triangle", tone: "danger", downloadable: true };
      default:
        return { ...none, text: "PDF wordt gemaakt bij download", icon: "pi pi-file", tone: "muted", downloadable: true };
    }
  };

  const downloadEntryPdf = async (entry: RapportListEntry) => {
    const key = getPdfKey(entry);
    const fresh = await downloadRapportPdf({
      rapportId: entry.rapport.id,
      inspectieType: entry.inspectieEntry.type,
      rapportNr: entry.rapport.inspectieRapportNr,
      document: pdfDocuments.value.get(key),
    });
    if (fresh) {
      const next = new Map(pdfDocuments.value);
      next.set(key, fresh);
      pdfDocuments.value = next;
    }
  };

  const openPaymentPage = (entry: RapportListEntry) => {
    router.push({ name: "RapportNaBetaling", query: { id: entry.rapport.id, type: entry.inspectieEntry.type } });
  };

  const fetchRapporten = async () => {
    await rapportenStore.fetchRapporten();
    const ids = (rapportenStore.rapporten as unknown as RapportCardRapport[]).map((rapport) => rapport.id);

    // Badge data only: a failed hold or PDF lookup must never block the list
    // (RLS still enforces the actual content block).
    await Promise.all([
      fetchRapportPaymentHolds(ids)
        .then((holds) => {
          holdsByRapportId.value = holds;
        })
        .catch((error) => console.error("Kon betaalblokkades niet laden:", error)),
      fetchRapportPdfDocuments(ids)
        .then((documents) => {
          pdfDocuments.value = documents;
        })
        .catch((error) => console.error("Kon rapport-PDF's niet laden:", error)),
    ]);
  };

  onMounted(() => {
    fetchRapporten();
  });
</script>

<template>
  <div class="rapporten-page">
    <AppHeader
      title="Rapporten"
      :show-back-button="true"
      back-to="/"
      back-text="Terug naar dashboard"
    />

    <!-- Filters and Search Panel -->
    <Panel
      class="filters-card"
      toggleable
      collapsed
      header="Zoeken en Filteren"
    >
      <div class="filters-stack">
        <!-- Search -->
        <div class="filters-search">
          <IconField>
            <InputIcon>
              <i class="pi pi-search" />
            </InputIcon>
            <InputText
              v-model="searchQuery"
              placeholder="Zoek op rapport nummer, locatie..."
              class="filters-input"
            />
          </IconField>
          <div
            v-if="searchQuery && filteredRapporten.length !== undefined"
            class="filters-meta"
          >
            {{ filteredRapporten.length }} resultaten gevonden
          </div>
        </div>

        <!-- Filters Row -->
        <div class="filters-row">
          <!-- Status Filter -->
          <div class="filters-select">
            <Select
              v-model="statusFilter"
              :options="statusOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Filter op status"
              class="w-full"
            />
          </div>

          <!-- Scope Filters -->
          <div class="filters-scopes">
            <ToggleButton
              v-model="scopeFilters.scope8"
              onLabel="Scope 8"
              offLabel="Scope 8"
              onIcon="pi pi-check"
              offIcon="pi pi-times"
              class="scope-toggle"
            />
            <ToggleButton
              v-model="scopeFilters.scope10"
              onLabel="Scope 10"
              offLabel="Scope 10"
              onIcon="pi pi-check"
              offIcon="pi pi-times"
              class="scope-toggle"
            />
            <ToggleButton
              v-model="scopeFilters.scope12"
              onLabel="Scope 12"
              offLabel="Scope 12"
              onIcon="pi pi-check"
              offIcon="pi pi-times"
              class="scope-toggle"
            />
          </div>

          <!-- Refresh Button -->
          <Button
            icon="pi pi-refresh"
            @click="fetchRapporten()"
            :loading="rapportenStore.loading"
            severity="secondary"
            outlined
            aria-label="Vernieuwen"
            class="filters-refresh"
          />
        </div>
      </div>
    </Panel>

    <!-- Loading State -->
    <div
      v-if="rapportenStore.loading"
      class="status-card status-loading"
    >
      <i class="pi pi-spin pi-spinner text-4xl"></i>
    </div>

    <!-- Error State -->
    <div
      v-else-if="rapportenStore.error"
      class="status-card status-error"
    >
      {{ rapportenStore.error }}
    </div>

    <!-- Empty State -->
    <div
      v-else-if="filteredRapporten.length === 0"
      class="status-card status-empty"
    >
      <template v-if="searchQuery"> Geen rapporten gevonden voor deze zoekopdracht. </template>
      <template v-else> Er zijn nog geen rapporten beschikbaar. </template>
    </div>

    <!-- Rapporten List -->
    <div v-else>
      <div class="rapporten-header">
        <span class="rapporten-count">{{ filteredRapporten.length }} inspecties gevonden</span>
        <Button
          icon="pi pi-refresh"
          @click="fetchRapporten()"
          :loading="rapportenStore.loading"
          severity="secondary"
          outlined
          aria-label="Vernieuwen"
          size="small"
        />
      </div>

      <div class="rapporten-grid">
        <div
          v-for="entry in displayedRapporten"
          :key="entry.id"
          class="rapport-card"
          @click="router.push(getRapportDetailsPath(entry.rapport.id, entry.inspectieEntry.type))"
        >
          <div class="rapport-card-header">
            <span class="rapport-nr">{{ entry.rapport.inspectieRapportNr || "Geen nummer" }}</span>
            <span class="rapport-date">{{ formatDate(entry.rapport.created_at) }}</span>
          </div>

          <div class="rapport-badges">
            <CustomTag
              :color="entry.inspectieEntry.type"
              :label="inspectieTypeLabels[entry.inspectieEntry.type]"
            />
            <CustomTag
              :color="entry.inspectieEntry.status"
              :label="statusLabels[entry.inspectieEntry.status]"
            />
            <CustomTag
              v-if="entry.inspectieEntry.certificaatBeschikbaar"
              color="voldoet"
              icon="pi pi-verified"
              label="Certificaat"
            />
            <CustomTag
              v-if="entry.vergrendeld"
              color="bezig"
              icon="pi pi-lock"
              :label="entry.paymentHold ? 'Na betaling' : 'Niet vrijgegeven'"
            />
          </div>

          <div
            v-if="(entry.rapport as any).locatie_snapshot"
            class="rapport-location"
          >
            <p class="location-name">{{ (entry.rapport as any).locatie_snapshot.naam }}</p>
            <p class="location-address">
              {{ (entry.rapport as any).locatie_snapshot.straat }} {{ (entry.rapport as any).locatie_snapshot.huisnr }},
              {{ (entry.rapport as any).locatie_snapshot.plaats }}
            </p>
          </div>

          <div
            class="rapport-pdf-row"
            @click.stop
          >
            <i
              :class="[getListPdfState(entry).icon, `tone-${getListPdfState(entry).tone}`]"
              class="rapport-pdf-icon"
              aria-hidden="true"
            />
            <span
              class="rapport-pdf-text"
              :class="`tone-${getListPdfState(entry).tone}`"
            >
              {{ getListPdfState(entry).text }}
            </span>
            <Button
              v-if="getListPdfState(entry).downloadable"
              label="PDF"
              icon="pi pi-download"
              :loading="pdfBusyKeys.has(getPdfKey(entry))"
              :aria-label="`Download rapport ${entry.rapport.inspectieRapportNr || ''} als PDF`"
              class="rapport-pdf-button"
              @click="downloadEntryPdf(entry)"
            />
            <Button
              v-else-if="getListPdfState(entry).payable"
              label="Betalen"
              severity="secondary"
              text
              @click="openPaymentPage(entry)"
            />
          </div>
        </div>
      </div>

      <div
        v-if="hasMore"
        class="rapporten-load-more"
        @click="loadMore"
      >
        <span class="rapporten-load-more-text"> <i class="pi pi-arrow-down" /> Meer laden... </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .rapporten-page {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding-bottom: 2rem;
  }

  .filters-card {
    background: transparent;
    border-radius: 0;
    border: none;
    box-shadow: none;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--p-background-200);
  }

  .filters-card :deep(.p-panel-header) {
    background: transparent;
    border-bottom: none;
    font-weight: 600;
    color: var(--p-background-800);
    padding: 0 0 0.75rem;
  }

  .filters-card :deep(.p-panel-content) {
    background: transparent;
    padding: 0;
  }

  .filters-card :deep(.p-panel-toggleable) {
    padding: 0;
  }

  .filters-stack {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .filters-search {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .filters-input {
    width: 100%;
  }

  .filters-meta {
    font-size: 0.85rem;
    color: var(--p-background-600);
  }

  .filters-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
    padding-top: 0.75rem;
    border-top: 1px solid var(--p-background-200);
  }

  .filters-select {
    flex: 1;
    min-width: 220px;
  }

  .filters-scopes {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .scope-toggle {
    min-width: 4.75rem;
    font-size: 0.85rem;
    border-radius: var(--radius-sm);
  }

  .filters-refresh {
    margin-left: auto;
  }

  .status-card {
    border-radius: var(--radius-sm);
    border: 1px solid var(--p-background-200);
    background: var(--p-background-0);
    padding: 1.25rem;
    text-align: center;
    color: var(--p-background-600);
  }

  .status-loading {
    background: var(--p-background-0);
  }

  .status-error {
    border-color: var(--p-red-200);
    background: var(--p-red-50);
    color: var(--p-red-600);
  }

  .rapporten-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--p-background-200);
    margin-bottom: 1.25rem;
  }

  .rapporten-count {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--p-background-600);
  }

  .rapporten-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 1.25rem;
  }

  .rapport-card {
    background: var(--p-background-0);
    border: 1px solid var(--p-background-200);
    border-radius: 0.5rem;
    padding: 1.25rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .rapport-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .rapport-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    gap: 0.75rem;
  }

  .rapport-nr {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--p-background-900);
    word-break: break-word;
  }

  .rapport-date {
    font-size: 0.8rem;
    color: var(--p-background-500);
    white-space: nowrap;
  }

  .rapport-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .rapport-location {
    padding-top: 1rem;
    border-top: 1px solid var(--p-background-200);
    font-size: 0.85rem;
    color: var(--p-background-600);
  }

  .location-name {
    font-weight: 500;
    color: var(--p-background-700);
    margin-bottom: 0.25rem;
  }

  .location-address {
    margin: 0;
  }

  .rapport-pdf-row {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    min-height: 3.25rem;
    margin: 1rem -1.25rem -1.25rem;
    padding: 0.375rem 0.5rem 0.375rem 1.25rem;
    border-top: 1px solid var(--p-background-200);
    cursor: default;
  }

  .rapport-pdf-icon {
    font-size: 0.95rem;
  }

  .rapport-pdf-text {
    flex: 1;
    min-width: 0;
    font-size: 0.85rem;
    color: var(--p-background-700);
  }

  .rapport-pdf-button {
    flex-shrink: 0;
    font-weight: 700;
  }

  .tone-success {
    color: var(--color-voldoet);
  }

  .tone-warning {
    color: var(--p-orange-700);
  }

  .tone-info {
    color: var(--p-blue-700);
  }

  .tone-danger {
    color: var(--p-red-700);
  }

  .tone-muted {
    color: var(--p-background-500);
  }

  .rapporten-load-more {
    margin-top: 1rem;
    padding: 0.75rem 0;
    border-top: 1px solid var(--p-background-200);
    text-align: center;
    cursor: pointer;
    color: var(--p-background-600);
    transition: color 0.2s ease;
  }

  .rapporten-load-more:hover {
    color: var(--p-primary-500);
  }

  .rapporten-load-more-text {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
  }

  /* Mobile responsive adjustments */
  @media (max-width: 640px) {
    .filters-refresh {
      margin-left: 0;
    }

    .rapporten-grid {
      grid-template-columns: 1fr;
    }

    .rapport-card-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .rapport-date {
      order: -1;
      margin-bottom: 0.25rem;
    }
  }
</style>
